import { useEffect, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useActiveOrgStore } from '~/domains/Organization/store'
import { getMyPermissions } from '~/services/team'
import useOrganizations from 'hooks/domains/Organization/useOrganizations'
import useAuth from 'hooks/useAuth'
import { getIdToken } from 'helpers/auth'

// Business logic for the active organization. The active id / setter live in
// the zustand store — consumers read those from `useActiveOrgStore` directly;
// this hook only owns the derived data (the org list) and the flows that act
// on the store (seed/reconcile on load, validate-then-commit switch).
//
// `enabled` (default true) gates the org-list fetch and its reconciliation
// effect — callers pass `false` when the multiple-roles flag is off, since
// there's nothing to switch between and the list shouldn't be requested.
export default function useActiveOrg(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useAuth()
  // Also gated on the session, not just the caller's flag: the header (and so
  // this hook) stays mounted through logout until the redirect lands, and
  // logout clears the query cache — clearing a cache that still has an active
  // observer makes it refetch. That fired GET /organizations with no token,
  // which 401s and trips the api-client's force-logout + router.reload path.
  //
  // The live getIdToken() read matters, not just isAuthenticated: logout's
  // dispatch is batched, so the context flag is still true at the moment the
  // cache is cleared, whereas setSession(null) has already nulled the token
  // synchronously. Same pattern the dashboard queries use.
  const enabled =
    (options?.enabled ?? true) && isAuthenticated && !!getIdToken()
  const { activeOrgId, setActiveOrgId } = useActiveOrgStore(
    useShallow((state) => ({
      activeOrgId: state.activeOrgId,
      setActiveOrgId: state.setActiveOrgId,
    })),
  )

  const { data, isLoading } = useOrganizations({ enabled })
  const organizations = useMemo(() => data?.organizations || [], [data])

  // Seed/reconcile the active org against the fetched list: pick the list's
  // current org (or the first) when nothing is stored, OR when the stored id
  // is no longer one the user belongs to (removed/deleted org) — otherwise the
  // interceptor would keep stamping a dead Org-Id forever.
  useEffect(() => {
    if (!enabled || organizations.length === 0) return
    const isKnown = organizations.some((org) => org.org_id === activeOrgId)
    if (activeOrgId && isKnown) return
    const current =
      organizations.find((org) => org.is_last_active) || organizations[0]
    setActiveOrgId(current.org_id)
  }, [enabled, activeOrgId, organizations, setActiveOrgId])

  // Switch the active org with a validate-then-commit flow: first confirm the
  // target org is accessible via /me (scoped to that org by an explicit
  // header, WITHOUT touching the globally-active org). Only once it succeeds
  // do we commit — persist the new active org. The caller is expected to
  // reload the page on success, so every store/query/component-local state
  // resets cleanly under it instead of relying on each one to react to the
  // switch correctly. If validation throws, nothing has changed, so there is
  // no revert and no window in which stale requests can bleed the new org's
  // data into the old context.
  const switchOrg = async (orgId: string) => {
    if (!orgId || orgId === activeOrgId) return

    // Validate the target org first (explicit Org-Id header on this one call,
    // which overrides the instance default). Throws to the caller (→ error
    // snackbar) if it isn't accessible; direct service call, so no React Query
    // retry inflates the wait.
    await getMyPermissions(orgId)

    // Commit: persist the new active org — the api-client interceptor reads
    // it from the store on every subsequent request.
    setActiveOrgId(orgId)
  }

  return { organizations, isLoading, switchOrg }
}
